import { Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { updateTrialData } from 'redux/slices/createTrial';
import {
  ablineAdjustmentSchema,
  ablineCreationSchema,
} from 'validators/createTrialSchemas';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import useAuth from 'hooks/useAuth';
import ABLineForm from 'components/create-trial/ABLineForm';
import {
  guessABLineHeading,
  createABLineFromHeading,
  createABLineGeoJSON,
  demoABLineGeoJSON,
  adjustABLine,
} from 'utils/ShapefileUtils';

import { StepProps } from '../../types/stepFlow.type';

export const ABLineCreationContainer = ({ children }: StepProps) => {
  return <ABLineContainer task={'creation'} children={children} />;
};

export const ABLineAdjustmentContainer = ({ children }: StepProps) => {
  return <ABLineContainer task={'adjustment'} children={children} />;
};

interface ABLineContainerProps {
  children?: React.ReactNode;
  task: 'creation' | 'adjustment';
}

const ABLineContainer = ({ children, task }: ABLineContainerProps) => {
  const { isGuest } = useAuth();
  const { trial } = useAppSelector((state) => state.createTrial);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();

  const memoizedTrialValues = useMemo(() => {
    return {
      field_boundary_geojson: trial.field_boundary_geojson,
      trial_boundary_geojson: trial.trial_boundary_geojson,
      get_abline_method: trial.get_abline_method,
      abline_adjust: trial.abline_adjust,
      abline_rotation: trial.abline_rotation,
      abline_heading: trial.abline_heading,
      abline_coordinates: trial.abline_coordinates,
      abline_geojson: trial.abline_geojson,
    };
  }, [
    trial.field_boundary_geojson,
    trial.trial_boundary_geojson,
    trial.get_abline_method,
    trial.abline_adjust,
    trial.abline_rotation,
    trial.abline_heading,
    trial.abline_coordinates,
    trial.abline_geojson,
  ]) as Record<string, any>;

  const validationSchema =
    task === 'creation' ? ablineCreationSchema : ablineAdjustmentSchema;

  // Local implementation of AB line creation
  const generateLocalABLine = useCallback(
    async (values: Record<string, any>) => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const fieldBoundaryGeoJSON = memoizedTrialValues.field_boundary_geojson;
        if (!fieldBoundaryGeoJSON) {
          throw new Error('Field boundary GeoJSON is required');
        }

        let abLineCoordinates;
        let abLineGeoJSON;

        // Check if we're using the demo field boundary
        const isUsingDemoField =
          trial.field_boundary_files_meta &&
          trial.field_boundary_files_meta.length === 1 &&
          trial.field_boundary_files_meta[0] === 'demo_field__boundary.geojson';

        // Handle different methods for AB line creation
        switch (values.get_abline_method) {
          case 'demo':
            // If using demo field, use the demo AB line
            if (isUsingDemoField) {
              // Use the pre-defined demo AB line
              abLineGeoJSON = demoABLineGeoJSON;
              // Extract the coordinates from the demo AB line
              abLineCoordinates =
                demoABLineGeoJSON.features[0].geometry.coordinates;

              // Extract the heading from the demo AB line metadata
              // The demo AB line has "AB Heading=1.387 deg" in its properties
              const abLineProperty =
                demoABLineGeoJSON.features[0].properties?.AB_Line;
              let demoHeading = '1.387'; // Default fallback
              if (abLineProperty) {
                const headingMatch = abLineProperty.match(
                  /AB Heading=([\d.]+) deg/,
                );
                if (headingMatch) {
                  demoHeading = headingMatch[1];
                }
              }
              if (!values.abline_heading) {
                values.abline_heading = demoHeading;
              }
            } else {
              throw new Error(
                'Demo AB line is only available with demo field boundary',
              );
            }
            break;

          case 'guess':
            // Use our local implementation to guess the optimal heading
            const heading = guessABLineHeading(fieldBoundaryGeoJSON);
            // Create AB line from the guessed heading
            abLineCoordinates = createABLineFromHeading(
              heading,
              fieldBoundaryGeoJSON,
            );
            // Create GeoJSON for the AB line
            abLineGeoJSON = createABLineGeoJSON(abLineCoordinates);
            break;

          case 'heading':
            // Create AB line from user-provided heading
            if (!values.abline_heading) {
              // Clear any previous AB line from the Redux store when heading is missing
              dispatch(
                updateTrialData({
                  abline_coordinates: undefined,
                  abline_geojson: undefined,
                }),
              );
              throw new Error('Please enter an AB line heading in degrees');
            }
            const userHeading = parseFloat(values.abline_heading);
            abLineCoordinates = createABLineFromHeading(
              userHeading,
              fieldBoundaryGeoJSON,
            );
            // Create GeoJSON for the AB line
            abLineGeoJSON = createABLineGeoJSON(abLineCoordinates);
            break;

          case 'upload':
            // Process uploaded AB line files
            if (
              !values.abline_files_meta ||
              values.abline_files_meta.length === 0
            ) {
              throw new Error('AB line files are required');
            }

            // Check if geojson was properly parsed during upload
            if (!values.abline_geojson) {
              throw new Error('AB line GeoJSON is required');
            }

            // Use the already parsed GeoJSON
            abLineGeoJSON = values.abline_geojson;

            // Extract coordinates from the GeoJSON
            if (
              !abLineGeoJSON.features ||
              abLineGeoJSON.features.length === 0 ||
              !abLineGeoJSON.features[0].geometry
            ) {
              throw new Error('Invalid AB line GeoJSON');
            }

            abLineCoordinates = abLineGeoJSON.features[0].geometry.coordinates;
            break;

          default:
            throw new Error('Invalid AB line method');
        }

        // Store the AB line GeoJSON in the Redux store
        dispatch(
          updateTrialData({
            abline_geojson: abLineGeoJSON,
            abline_coordinates: abLineCoordinates,
            abline_method: values.get_abline_method,
            abline_heading: values.abline_heading,
          }),
        );

        setIsLoading(false);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to create AB line',
        );
        setIsLoading(false);
      }
    },
    [
      dispatch,
      memoizedTrialValues.field_boundary_geojson,
      trial.field_boundary_files_meta,
    ],
  );

  // Function to handle client-side AB line adjustments
  const adjustLocalABLine = useCallback(
    (values: Record<string, any>) => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        // Get the original coordinates from the state
        const originalCoordinates = memoizedTrialValues.abline_coordinates;
        if (!originalCoordinates) {
          throw new Error('AB line coordinates are required for adjustment');
        }

        // Get the adjustment values
        const rotation = parseFloat(values.abline_rotation || '0');
        const translation = parseFloat(values.abline_adjust || '0');

        // Adjust the AB line
        const adjustedCoordinates = adjustABLine(
          originalCoordinates,
          rotation,
          translation,
        );

        // Create GeoJSON for the adjusted AB line
        const adjustedGeoJSON = createABLineGeoJSON(adjustedCoordinates);

        // Store the adjusted AB line in the Redux store
        dispatch(
          updateTrialData({
            abline_adjusted_coordinates: adjustedCoordinates,
            abline_adjusted_geojson: adjustedGeoJSON,
            abline_rotation: rotation,
            abline_adjust: translation,
          }),
        );

        setIsLoading(false);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to adjust AB line',
        );
        setIsLoading(false);
      }
    },
    [dispatch, memoizedTrialValues.abline_coordinates],
  );

  const generatePreview = useCallback(
    (values?: Record<string, any>) => {
      // Reset error message
      setErrorMessage(null);

      if (values) {
        // Store the values in Redux for both tasks
        const updateData: Record<string, any> = {};

        if (values.get_abline_method) {
          updateData.get_abline_method = values.get_abline_method;
        }

        if (values.abline_rotation !== undefined) {
          updateData.abline_rotation = values.abline_rotation;
        }

        if (values.abline_adjust !== undefined) {
          updateData.abline_adjust = values.abline_adjust;
        }

        if (values.abline_files_meta) {
          updateData.abline_files_meta = values.abline_files_meta;
        }

        if (values.abline_geojson) {
          updateData.abline_geojson = values.abline_geojson;
        }

        dispatch(updateTrialData(updateData));
      }

      if (task === 'creation') {
        // Use our local implementation for creation
        if (values) {
          generateLocalABLine(values);
        }
      } else {
        // Use our local implementation for adjustment too
        if (values) {
          adjustLocalABLine(values);
        }
      }
    },
    [dispatch, task, generateLocalABLine, adjustLocalABLine],
  );

  return (
    <Formik
      initialValues={{
        get_abline_method: trial?.get_abline_method || 'guess',
        abline_files_meta: trial?.abline_files_meta || [],
        abline_geojson: trial?.abline_geojson || null,
        isGuest,
        abline_adjust: trial?.abline_adjust || 0,
        abline_rotation: trial?.abline_rotation || 0,
        abline_heading: trial?.abline_heading || '',
      }}
      onSubmit={() => {}}
      validationSchema={validationSchema}
      validateOnMount
    >
      <>
        <ABLineForm
          imageUrl=""
          isLoading={isLoading}
          task={task}
          generatePreview={generatePreview}
          error={errorMessage}
        />
        {children}
      </>
    </Formik>
  );
};
